import { useState } from 'preact/hooks';
import { useApp } from '../../app/context';
import type { LayoutPreference } from '../../game/contract';
import { ru } from '../../i18n/ru';
import { gamepads } from '../../input/gamepads';
import { LAYOUT_PREFERENCES } from '../../state/settings';
import { Button } from '../components/Button';
import { Segmented, type SegmentedOption, Slider, Toggle } from '../components/controls';
import { Dialog } from '../components/Dialog';
import { canVibrate } from '../dom';
import './SettingsDialog.css';

const LAYOUT_OPTIONS: ReadonlyArray<SegmentedOption<LayoutPreference>> = LAYOUT_PREFERENCES.map(
  (value) => ({ value, label: ru.settings.layoutOptions[value] }),
);

export function SettingsDialog({ onClose }: { onClose(): void }) {
  const app = useApp();
  const settings = app.settings.settings.value;
  const calendar = app.settings.calendar.value;
  const update = app.settings.update.bind(app.settings);
  const inGame = app.screen.value === 'game';
  const [confirmLogout, setConfirmLogout] = useState(false);

  const logout = () => {
    if (!confirmLogout) {
      setConfirmLogout(true);
      return;
    }
    onClose();
    app.logout();
  };

  return (
    <Dialog title={ru.settings.title} onClose={onClose} wide>
      <div class="settings">
        <Slider
          icon="🎵"
          label={ru.settings.music}
          value={settings.musicVolume}
          onChange={(musicVolume) => update({ musicVolume })}
        />
        <Slider
          icon="🔊"
          label={ru.settings.sfx}
          value={settings.sfxVolume}
          onChange={(sfxVolume) => update({ sfxVolume })}
        />
        <Slider
          icon="🗣️"
          label={ru.settings.voices}
          value={settings.voiceVolume}
          onChange={(voiceVolume) => update({ voiceVolume })}
        />
        <div class="settings__group">
          <div class="settings__label">
            <span aria-hidden="true">🖥️</span> {ru.settings.layout}
          </div>
          <Segmented
            label={ru.settings.layout}
            options={LAYOUT_OPTIONS}
            value={settings.layout}
            onChange={(layout) => update({ layout })}
          />
          <p class="settings__hint">{ru.settings.layoutHints[settings.layout]}</p>
        </div>
        <Toggle
          icon="👻"
          label={ru.settings.ghosts}
          checked={settings.showGhosts}
          onChange={(showGhosts) => update({ showGhosts })}
        />
        {/* A switch that does nothing would only puzzle: offered where the device can buzz, or
            a gamepad in use can rumble (on a crash). */}
        {(canVibrate() || gamepads.style.value !== null) && (
          <Toggle
            icon="📳"
            label={ru.settings.haptics}
            checked={settings.haptics}
            onChange={(haptics) => update({ haptics })}
          />
        )}
        {/* Holiday decorations and tunes: only while a holiday is on (out of season the switch
            would change nothing), and for that holiday only (the next one asks again). */}
        {calendar !== 'none' && (
          <Toggle
            icon={ru.season.icons[calendar]}
            label={ru.season.toggle}
            checked={settings.festive[calendar]}
            onChange={(on) => app.settings.setFestive(calendar, on)}
          />
        )}
        {/* Logging out mid-race would silently abandon it, so it is offered outside races only. */}
        {!inGame && (
          <Button variant="red" block icon="🚪" class="settings__logout" onClick={logout}>
            {confirmLogout ? ru.settings.logoutConfirm : ru.settings.logout}
          </Button>
        )}
      </div>
    </Dialog>
  );
}
